// Remove noindex + banner once Hometree Finance confirms written approval of this copy.
import { useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  CalendarCheck,
  CalendarClock,
  ClipboardCheck,
  FileText,
  Gauge,
  Home,
  Info,
  KeyRound,
  Landmark,
  PoundSterling,
  ShieldCheck,
  Sun,
  TrendingUp,
  Unlock,
  Users,
  Wallet,
  Wrench,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link } from "@tanstack/react-router";

import heroFinanceAsset from "@/assets/financing/finance-solar-home.jpg.asset.json";
import { FinancingEnquiryForm } from "@/components/financing-enquiry-form";
import { HometreePartnerBanner } from "@/components/financing/hometree-partner-banner";
import { JourneyTimeline } from "@/components/financing/journey-timeline";
import { HometreeLogo } from "@/components/financing/hometree-logo";
import { AccreditationStrip, Section } from "@/components/site-blocks";
import {
  SOLAR_LEASE_DISCLOSURE,
  SOLAR_LEASE_ELIGIBILITY,
  SOLAR_LEASE_ELIGIBILITY_INTRO,
  SOLAR_LEASE_FAQS,
  SOLAR_LEASE_HERO,
  SOLAR_LEASE_HIGHLIGHTS,
  SOLAR_LEASE_JOURNEY_STEPS,
  SOLAR_LEASE_PLAN_PATH,
  SOLAR_LEASE_THINGS_TO_KNOW,
  SOLAR_LEASE_WHAT_YOU_GET,
} from "@/content/financing-solar-lease-plan";
import { FINANCING_I4F_HOW_IT_WORKS_PATH } from "@/content/financing-i4f-how-it-works";

function LeaseFaqAccordion() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="space-y-3 max-w-3xl">
      {SOLAR_LEASE_FAQS.filter((f) => f.a).map((faq, i) => (
        <div key={faq.q} className="rounded-xl border bg-card overflow-hidden">
          <button
            type="button"
            className="w-full text-left px-5 py-4 flex items-center justify-between gap-3 hover:bg-secondary/50 transition-colors"
            onClick={() => setOpen(open === i ? null : i)}
            aria-expanded={open === i}
          >
            <span className="font-semibold">{faq.q}</span>
            <span className="text-2xl text-accent leading-none">{open === i ? "−" : "+"}</span>
          </button>
          {open === i && faq.a ? (
            <div className="px-5 pb-5 text-muted-foreground border-t">
              <p className="pt-3">{faq.a}</p>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** Icons paired positionally with SOLAR_LEASE_WHAT_YOU_GET items. */
const WHAT_YOU_GET_ICONS: LucideIcon[] = [
  Wallet,
  CalendarClock,
  Wrench,
  ShieldCheck,
  Sun,
  Unlock,
  KeyRound,
];

/** Icons paired positionally with SOLAR_LEASE_THINGS_TO_KNOW items. */
const THINGS_TO_KNOW_ICONS: LucideIcon[] = [
  Info,
  TrendingUp,
  FileText,
  AlertCircle,
  Users,
  Gauge,
];

/** Icons paired positionally with SOLAR_LEASE_ELIGIBILITY items. */
const ELIGIBILITY_ICONS: LucideIcon[] = [Home, CalendarCheck, PoundSterling, ClipboardCheck];

type FeatureCardProps = {
  icon: LucideIcon;
  title: string;
  description: string;
  /** Tailwind classes for the circular icon background tint. */
  iconBg: string;
  iconColor: string;
};

function FeatureCard({
  icon: Icon,
  title,
  description,
  iconBg,
  iconColor,
}: FeatureCardProps) {
  return (
    <li className="rounded-xl border border-border bg-background p-4 md:p-5">
      <div className="flex items-start gap-3">
        <span
          className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${iconBg}`}
        >
          <Icon className={`h-5 w-5 ${iconColor}`} aria-hidden />
        </span>
        <div className="min-w-0">
          <h3 className="text-sm md:text-base font-bold text-foreground leading-snug">
            {title}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
            {description}
          </p>
        </div>
      </div>
    </li>
  );
}

function FeatureCardColumn({
  heading,
  items,
  icons,
  iconBg,
  iconColor,
}: {
  heading: string;
  items: readonly { title: string; description: string }[];
  icons: LucideIcon[];
  iconBg: string;
  iconColor: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-soft">
      <h2 className="text-sm uppercase font-bold tracking-wider text-accent mb-4">
        {heading}
      </h2>
      <ul className="space-y-3">
        {items.map((item, i) => (
          <FeatureCard
            key={item.title}
            icon={icons[i] ?? Info}
            title={item.title}
            description={item.description}
            iconBg={iconBg}
            iconColor={iconColor}
          />
        ))}
      </ul>
    </div>
  );
}

export function FinancingSolarLeasePlanPage() {
  return (
    <>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroFinanceAsset.url}
            alt="Residential home with solar panels"
            className="w-full h-full object-cover object-center"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            width={1920}
            height={1080}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary/60" />
        </div>
        <div className="container-page relative py-16 md:py-24 text-primary-foreground">
          <div className="mb-3">
            <HometreeLogo variant="white" className="h-5 md:h-6" />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold max-w-4xl text-balance">
            {SOLAR_LEASE_HERO.h1}
          </h1>
          <p className="mt-5 text-lg md:text-xl text-primary-foreground/85 max-w-3xl leading-relaxed">
            {SOLAR_LEASE_HERO.body}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
            <a
              href="#the-journey"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3.5 font-semibold shadow-cta hover:bg-accent/90 transition-colors w-full sm:w-auto"
            >
              See How The Solar Plan Works
              <ArrowRight className="h-4 w-4" aria-hidden />
            </a>
            <Link
              to={FINANCING_I4F_HOW_IT_WORKS_PATH}
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-primary-foreground/40 px-6 py-3.5 font-semibold text-primary-foreground hover:bg-primary-foreground/10 transition-colors w-full sm:w-auto"
            >
              Compare This To Loan Finance
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>

          <p className="mt-4 text-xs text-primary-foreground/80 max-w-2xl leading-relaxed">
            {SOLAR_LEASE_HERO.smallPrint}
          </p>
        </div>
      </section>

      {/* Factual plan highlights - numbers with short labels */}
      <section className="border-b border-border/55 bg-secondary/25 py-6 md:py-8">
        <div className="container-page max-w-4xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {SOLAR_LEASE_HIGHLIGHTS.map((stat) => (
              <div
                key={stat.label}
                className="rounded-lg border border-border bg-card p-4 md:p-5 flex flex-col items-center text-center"
              >
                <span className="text-2xl md:text-4xl font-bold text-accent leading-none">
                  {stat.value}
                </span>
                <span className="mt-2 text-xs uppercase tracking-wide text-muted-foreground leading-snug">
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner banner - Hometree Finance logo on navy, matching the Loan Finance page */}
      <section className="border-b border-border/55 bg-background py-6 md:py-8">
        <div className="container-page max-w-4xl">
          <HometreePartnerBanner />
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-b border-border/55 bg-secondary/25 py-6 md:py-8">
        <div className="container-page">
          <AccreditationStrip />
        </div>
      </section>

      {/* Explainer video */}
      <Section className="bg-background py-12 md:py-16">
        <div className="max-w-3xl mb-6 md:mb-8">
          <p className="text-xs uppercase font-bold tracking-wider text-accent mb-3">
            Watch
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">
            The Solar Plan Explained
          </h2>
        </div>
        <div className="relative w-full overflow-hidden rounded-2xl border border-border shadow-soft bg-black">
          <div className="relative aspect-video">
            <iframe
              className="absolute inset-0 h-full w-full"
              src="https://www.youtube-nocookie.com/embed/S4rtbOVfJ0Q?rel=0"
              title="The Hometree Solar Plan Explained"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>
      </Section>

      {/* The journey */}
      <Section id="the-journey" className="bg-card py-12 md:py-16">
        <div className="max-w-3xl mb-8 md:mb-10">
          <p className="text-xs uppercase font-bold tracking-wider text-accent mb-3">The Journey</p>
        </div>
        <JourneyTimeline steps={SOLAR_LEASE_JOURNEY_STEPS} />
      </Section>

      {/* What you get / Important things to know - equal prominence (compliance) */}
      <Section className="py-12 md:py-16">
        <div className="grid gap-6 md:grid-cols-2 md:gap-8 items-start">
          <FeatureCardColumn
            heading="What You Get"
            items={SOLAR_LEASE_WHAT_YOU_GET}
            icons={WHAT_YOU_GET_ICONS}
            iconBg="bg-accent/10"
            iconColor="text-accent"
          />
          <FeatureCardColumn
            heading="Important Things To Know"
            items={SOLAR_LEASE_THINGS_TO_KNOW}
            icons={THINGS_TO_KNOW_ICONS}
            iconBg="bg-muted"
            iconColor="text-foreground/80"
          />
        </div>
      </Section>

      {/* Eligibility */}
      <Section className="bg-secondary/30 py-12 md:py-16">
        <div className="max-w-3xl mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
            Is The Solar Plan Available To You?
          </h2>
          <p className="text-base text-foreground/90 leading-relaxed">
            {SOLAR_LEASE_ELIGIBILITY_INTRO}
          </p>
        </div>
        <div className="grid gap-3 md:gap-4 sm:grid-cols-2">
          {SOLAR_LEASE_ELIGIBILITY.map((fact, i) => {
            const Icon = ELIGIBILITY_ICONS[i] ?? Info;
            return (
              <div
                key={fact}
                className="rounded-xl border border-border bg-card p-4 md:p-5 shadow-soft"
              >
                <div className="flex items-start gap-3">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/10">
                    <Icon className="h-5 w-5 text-accent" aria-hidden />
                  </span>
                  <p className="text-sm md:text-base text-foreground/90 leading-relaxed">
                    {fact}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* FAQs */}
      <Section className="py-12 md:py-16">
        <div className="max-w-3xl mb-8">
          <p className="text-xs uppercase font-bold tracking-wider text-accent mb-3">FAQs</p>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">Common Questions</h2>
        </div>
        <LeaseFaqAccordion />
      </Section>

      {/* Closing CTA */}
      <section className="border-t border-border/50 bg-primary text-primary-foreground py-12 md:py-16">
        <div className="container-page max-w-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Ready To Find Out More?</h2>
          <p className="text-base text-primary-foreground/90 leading-relaxed">
            Get your written CRS Energy solar quote first - that&apos;s what your Solar Plan quote
            will be based on.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#solar-lease-quote"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3.5 font-semibold shadow-cta hover:bg-accent/90 transition-colors w-full sm:w-auto"
            >
              Get Your Solar Lease Quote
              <ArrowRight className="h-4 w-4" aria-hidden />
            </a>
          </div>
          <p className="mt-5">
            <Link
              to={FINANCING_I4F_HOW_IT_WORKS_PATH}
              className="inline-flex items-center gap-1.5 text-base font-semibold text-accent hover:text-accent/80 transition-colors"
            >
              Prefer a loan, or need finance for a non-solar project? See Loan Finance with
              Ideal4Finance
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </p>
        </div>
      </section>

      {/* Quote form */}
      <Section id="solar-lease-quote" className="py-12 md:py-16">
        <div className="max-w-2xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            Get Your Solar Lease Quote
          </h2>
          <FinancingEnquiryForm idPrefix="slp" sourcePage={SOLAR_LEASE_PLAN_PATH} />
        </div>
      </Section>

      {/* TODO: confirm with Hometree Finance whether they want an FRN or additional legal entity detail added here, matching the Ideal4Finance disclosure format. */}
      <section className="bg-secondary/40 py-10 md:py-12">
        <div className="container-page max-w-4xl">
          <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
            <div className="flex items-start gap-3 mb-3">
              <Landmark className="h-5 w-5 text-primary shrink-0" aria-hidden />
              <p className="text-sm uppercase font-bold tracking-wider text-primary">
                Regulatory Disclosure
              </p>
            </div>
            <div className="space-y-4">
              {SOLAR_LEASE_DISCLOSURE.map((line) => (
                <p key={line} className="text-base text-foreground leading-relaxed">
                  {line}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
